<template>
  <suspense>
    <template #default>
      <transition appear :duration="250" name="nested">
        <component
          :is="dialog.type === 'panel' ? VfPanel : VfModal"
          v-if="dialog"
          v-slot="attrs"
          :class-content="dialog.classContent"
          :data-test-id="`${dialog.type}-${dialog.name}`"
          :size="dialog.size"
          :style="{
            zIndex: dialog.depth ? depthConfig[dialog.depth] : depthConfig.sm,
          }"
          :type="dialog.type"
          @resolve="closeDialog"
        >
          <component
            :is="`dialog-${dialog.name}`"
            v-bind="{ ...attrs, ...dialog.props }"
            @resolve="closeDialog"
          />
        </component>
      </transition>
    </template>
    <template #fallback>
      <div style="min-height: 13.5rem">
        <vf-page-loader />
      </div>
    </template>
  </suspense>
  <vf-page-loader v-if="isLoading" />
</template>

<script lang="ts" setup>
import type { CSSClass } from '#types/common'
import type { DialogContent } from '#types/components/cms/dialog'
import type { DialogName, DialogSize, DialogType } from '#types/composables/useDialog'
import type { DialogDepth, DialogGuardAction, DialogGuardRule, DialogGuards } from '#types/config/components/dialog'
import { VfModal, VfPanel } from '#components'

const { $feature } = useNuxtApp()
const router = useRouter()
const route = useRoute()
const { getModal, getSections, isPreview } = useCms()
const { loggedIn } = storeToRefs(useAuthStore())
const appConfig = useAppConfig()
const { loyaltyToken, updateLoyaltyToken } = useLoyalty()
const { depth: depthConfig } = appConfig.components.dialog.default

const isLoading = ref(false)

const dialog = ref<{
  type?: 'panel' | 'modal'
  name?: string
  props?: Record<string, any>
  size?: DialogSize
  classContent?: CSSClass
  depth?: DialogDepth
} | null>(null)
const typeRef = ref<'modal' | 'panel'>()

provide(InjectKeyDialog, { type: typeRef as any })

const initialData = {
  'bopis-store-search': async () => ({ props: { filterBopisStores: true, showSaveButton: true } }),
  'sign-in': async (param: string) => param ? { props: { formLocation: param } } : {},
  'sign-up': async (param: string) => {
    const eSpots = (await getSections.$signUpModal())?.content.sectionsMap

    return {
      props: {
        eSpot: eSpots?.['below-sign-up'],
        ...(param && { formLocation: param })
      },
      size: eSpots?.['below-sign-up'] ? 'md' : 'xs'
    }
  }
}

const dialogGuardRules: Array<{
  name: DialogGuardRule
  condition: (parameter?: string) => Promise<boolean> | boolean
}> = [
  { name: 'flag', condition: (flag) => !!flag && !$feature[flag] },
  { name: 'guest', condition: () => !loggedIn.value },
  {
    name: 'loyaltyMember',
    condition: async () => {
      if (!loggedIn.value) return false
      await updateLoyaltyToken()
      return !!loyaltyToken.value.memberToken
    }
  },
  { name: 'registered', condition: () => !!loggedIn.value }
]

const dialogGuardActions: Record<DialogGuardAction, (...rest: any[]) => void> = {
  block: () => router.replace({ hash: undefined, query: route.query }),
  redirect: (target: string) => router.push({ hash: undefined, query: route.query, path: target }),
  replace: (target: string) => router.push({ hash: `#${typeRef.value}=${target}` }),
}

const handleDialogGuardActions = async (guards: NonNullable<DialogGuards>): Promise<boolean> => {
  const guardRuleConditions = dialogGuardRules.map(async (rule) =>
    [rule.name, !!guards[rule.name] && (await rule.condition(guards[rule.name].parameter))]
  )

  const guardRuleResultsArr = await Promise.all(guardRuleConditions)
  const guardRuleResults = Object.fromEntries(guardRuleResultsArr)

  const guardActions = dialogGuardRules
    .filter((rule) => guardRuleResults[rule.name])
    .map(({ name }) => guards[name])
  if (guardActions.length) {
    guardActions.forEach(({ action, target }) => dialogGuardActions[action](target))
    return true
  }
  return false
}

async function getDialog(hash: string) {
  const startingIndex = hash.search(/#modal=|#panel=/)

  // return early if hash does not include #modal= or #panel=
  if (startingIndex === -1) return

  const [type, nameWithPossibleQueryString = ''] = hash.substring(startingIndex + 1).split('=') as [DialogType, string]
  const [nameWithoutQueryString] = nameWithPossibleQueryString.split('?')
  const [name, param] = nameWithoutQueryString.split('_') as [DialogName, string | undefined]
  const { guards, dialogOptions } = appConfig.components.dialog[capitalize(name, { split: '-', join: '', isCamel: true })] || {}
  typeRef.value = type
  if (guards && (await handleDialogGuardActions(guards))) return // no need to further process and trigger dialog

  if (name === 'content' && param) {
    isLoading.value = true
    const content = await getModal<DialogContent>(param)
    isLoading.value = false

    return {
      type,
      name,
      props: { content },
      size: content.modalSize,
      classContent: content.modalSize === 'full' ? 'p-0' : undefined,
      depth: dialogOptions?.depth
    }
  }

  /* Quick Shop or Barcode - pass a product ID as the param to trigger the interface
   * Example: #modal=quickshop_TB0A161G231 or #modal=barcode_TB0A161G231
   */
  if (['barcode', 'quickshop'].includes(name) && param) {
    if (!PRODUCT_ID_REGEX.test(param)) throw new Error(`Invalid Product ID: ${param}`)
    return {
      type,
      name,
      props: { productId: param },
      size: { sm: 'full', lg: 'lg' },
      depth: dialogOptions?.depth
    }
  }
  const initial = await initialData[name]?.(param)

  return { type, name, ...initial }
}

const getDialogs = async (hash) => {
  try {
    if (!hash) {
      dialog.value = null
      return
    }

    const data = await getDialog(hash)

    if (!data) {
      dialog.value = null
      return
    }

    await closeDialogs()

    if (dialog.value) {
      dialog.value = null
      await nextTick()
    }

    dialog.value = data
  }
  catch (err) {
    log.error(err)
  }
}

let destroyAfterFn

if (isPreview) {
  await getDialogs(route.hash)
  destroyAfterFn = router.afterEach(async ({ hash }) => {
    await getDialogs(hash)
  })
}
else {
  watchImmediate(() => route.hash, getDialogs)
}

onBeforeUnmount(() => destroyAfterFn?.())

const closeDialog = () => {
  router.replace({ hash: undefined, query: route.query })
  dialog.value = null
}
</script>
